import React, { createContext, useContext, useMemo, useState, useCallback } from 'react';
import type { Supplier } from '@/types/supplier';
import { DEFAULT_SUPPLIER, isSupplier } from '@/types/supplier';

interface SupplierContextValue {
  supplier: Supplier;
  setSupplier: (supplier: Supplier) => void;
}

const STORAGE_KEY = 'product-center-supplier';

const SupplierContext = createContext<SupplierContextValue | undefined>(undefined);

function readStoredSupplier(): Supplier {
  const stored = localStorage.getItem(STORAGE_KEY);
  return isSupplier(stored) ? stored : DEFAULT_SUPPLIER;
}

export const SupplierProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [supplier, setSupplierState] = useState<Supplier>(readStoredSupplier);

  const setSupplier = useCallback((next: Supplier) => {
    setSupplierState(next);
    localStorage.setItem(STORAGE_KEY, next);
  }, []);

  const value = useMemo(() => ({ supplier, setSupplier }), [supplier, setSupplier]);

  return <SupplierContext.Provider value={value}>{children}</SupplierContext.Provider>;
};

export function useSupplier(): SupplierContextValue {
  const context = useContext(SupplierContext);
  if (!context) {
    throw new Error('useSupplier must be used within SupplierProvider');
  }
  return context;
}
